import React from 'react';
import styled from 'astroturf/react';
import { Heading } from 'js/design_system/Heading';
import { Body } from 'js/design_system/Body';
import { Icon } from 'js/design_system/Icon';

const InfoBanner = styled.div`
  background: var(--brand-colors-blue-100);
  border: 1px solid var(--brand-colors-blue-400);
  border-radius: var(--border-radius-radius-md);
  padding: var(--spacing-sm-24);
  margin-bottom: var(--spacing-sm-32);
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs-12);
  margin-bottom: var(--spacing-xs-12);
`;

const InfoIcon = styled(Icon)`
  color: var(--brand-colors-blue-600);
  flex-shrink: 0;
  margin-top: 2px;
`;

const InfoTitle = styled(Heading).attrs({
  size: 'sm',
  as: 'h3',
})`
  margin: 0;
  color: var(--brand-colors-blue-800);
`;

const InfoDescription = styled(Body).attrs({
  size: 'md',
})`
  color: var(--brand-colors-blue-800);
  margin-bottom: var(--spacing-xs-16);
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-xs-12);

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Feature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs-8);
  font-size: var(--type-body-sm-size);
  color: var(--brand-colors-blue-800);
`;

const FeatureDot = styled.div`
  width: var(--spacing-xs-4);
  height: var(--spacing-xs-4);
  background: var(--brand-colors-blue-600);
  border-radius: 50%;
  margin-top: var(--spacing-xs-8);
  flex-shrink: 0;
`;

interface CiterightInfoBannerProps {
  title?: string;
  description: string;
  features?: string[];
}

const DEFAULT_FEATURES = [
  'Auto-extracts claims',
  'Groups by context',
  'Export references',
  'No hallucinations',
];

export const CiterightInfoBanner: React.FC<CiterightInfoBannerProps> = ({
  title = 'How it works',
  description,
  features = DEFAULT_FEATURES,
}) => {
  return (
    <InfoBanner>
      <InfoHeader>
        <InfoIcon name="lightbulb" size={24} />
        <InfoTitle>{title}</InfoTitle>
      </InfoHeader>
      <InfoDescription>{description}</InfoDescription>
      <FeatureGrid>
        {features.map((feature, index) => (
          <Feature key={index}>
            <FeatureDot />
            <span>{feature}</span>
          </Feature>
        ))}
      </FeatureGrid>
    </InfoBanner>
  );
};
